import { isDefined } from '@leaflink/stash/useValidation';
import round from 'lodash-es/round';
import { unref } from 'vue';

import { ROUND_PRECISION } from '@/constants';
import i18n from '@/plugins/i18n';

import { Validator } from './types';

const required: Validator = {
  $validator: (value: unknown) => {
    if (typeof value === 'string') {
      value = value.trim();
    }

    return isDefined(value);
  },
  $message: i18n.t('validation.required'),
};

const positiveNumber: Validator = {
  $validator: (value: unknown) => !isDefined(value) || Number(value) > 0,
  $message: i18n.t('validation.positiveNumber'),
};

const wholeNumber: Validator = {
  $validator: (value: unknown) => {
    return !isDefined(value) || (Number.isInteger(Number(value)) && Number(value) >= 0);
  },
  $message: i18n.t('validation.wholeNumber'),
};

/**
 * @see: https://github.com/vuelidate/vuelidate/blob/62ba19d0c988abd54cef5880cbb22a8e8f9122de/packages/validators/src/raw/minValue.js#L9
 */
const minValue = (min: number) => {
  return {
    $validator: (value) => {
      return !isDefined(value) || ((!/\s/.test(value) || value instanceof Date) && +value >= +unref(min));
    },
    $message: i18n.t('validation.minVal', { value: min }),
  } as Validator;
};

/**
 * @see: https://github.com/vuelidate/vuelidate/blob/62ba19d0c988abd54cef5880cbb22a8e8f9122de/packages/validators/src/raw/maxValue.js#L9
 */
const maxValue = (max: number) => {
  return {
    $validator: (value) => {
      return !isDefined(value) || ((!/\s/.test(value) || value instanceof Date) && +value <= +unref(max));
    },
    $message: i18n.t('validation.maxVal', { value: max }),
  } as Validator;
};

const greaterThanOrEqualToField = ({ field, message }) =>
  ({
    $validator: (value, form) => {
      const fieldValue = form[field].$model.value;

      return !isDefined(fieldValue) || !isDefined(value) || value >= fieldValue;
    },
    $message: message,
  } as Validator);

const lessThanOrEqualToField = ({ field, message }) =>
  ({
    $validator: (value, form) => {
      const fieldValue = form[field].$model.value;

      return !isDefined(fieldValue) || !isDefined(value) || value <= fieldValue;
    },
    $message: message,
  } as Validator);

const validPrice = {
  $validator: (value: unknown) => {
    const price = Number(value);

    return !isDefined(value) || price === round(price, ROUND_PRECISION.CURRENCY);
  },
  $message: i18n.t('validation.validPrice'),
};

const email = {
  $validator: (value) => {
    return !isDefined(value) || /.+@.+\..+/.test(value);
  },
  $message: i18n.t('validation.email'),
};

/**
 * We can allow users to be flexible in the format they enter as long
 * as the normalized phone number contains exactly 10 digits. This could be
 * made more flexible in the future, but the current registration endpoint
 * expects an unformatted 10 digit number for the phone value.
 */
const phone = {
  $validator: (value) => {
    return !isDefined(value) || /^\D*\d{3}\D*\d{3}\D*\d{4}$/.test(value);
  },
  $message: i18n.t('validation.phone'),
};

/**
 * Since `required` and the `isDefined` helper treat `false` as a non-empty
 * value, use `isChecked` to enforce that a checkbox is in a checked state.
 * Using `required` with a checkbox can cause confusing UX. For example:
 *
 * 1. Checkbox has initial value of undefined. `required` fails.
 * 2. User checks checkbox, setting value to true. `required` passes.
 * 3. User unchecks checkbox, setting value to false. `required` passes.
 *
 * From a user perspective, it is very unclear why step 3 would behave
 * differently from step 1.
 */
const isChecked = {
  $validator: (value) => {
    return value === true;
  },
  $message: i18n.t('validation.required'),
} as Validator;

export {
  email,
  greaterThanOrEqualToField,
  isChecked,
  lessThanOrEqualToField,
  maxValue,
  minValue,
  phone,
  positiveNumber,
  required,
  validPrice,
  wholeNumber,
};
